import {CoreObjectId} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType, getObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import isCollaboratable from 'auth/isCollaboratable';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {SavingCollaborationManagementModal} from 'collaborator-components/CollaborationManagementModal';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import makeCancelable from 'lib/makeCancelable';
import runModal from 'ui-library/runModal';

import PoliceableDAO from './PoliceableDAO';
import {Collaboration} from './useCollaborationManagementState';

/*
 * Removes a single collaboration from a "policeable" (project & folder supported currently).
 *
 * If the user cancels the update by closing the data modification modal, this method will return a rejected
 * promise with `isCanceled = True`.
 */
export function removeCollaborator(policeableId: string, partyId: string): Promise<{}> {
  const policeableType = getObjectType({id: policeableId as CoreObjectId});
  return updatePoliceableWithDataModifications(
    (dataModsHeader?: DataModificationsHeader) =>
      PoliceableDAO.removeCollaborator(
        policeableId,
        partyId,
        getApiBaseForObjectType(policeableType),
        dataModsHeader
      ),
    policeableId
  );
}

/**
 * Updates the collaborations for a "policeable" via a full replacement (will remove existing collaborations
 * if not specified).
 */
export function updateCollaborations(
  policeableId: string,
  collaborations: Array<Collaboration>
): Promise<{}> {
  assert(
    isCollaboratable({id: policeableId}),
    `Object with ID ${policeableId} may not support capability. Did you add your type to CoreObjectMappings?`
  );
  const policeableType = getObjectType({id: policeableId as CoreObjectId});
  return updatePoliceableWithDataModifications(
    (dataModsHeader?: DataModificationsHeader) =>
      PoliceableDAO.updateCollaborations(
        policeableId,
        convertToCollaborationCreateOrUpdates(collaborations),
        getApiBaseForObjectType(policeableType),
        dataModsHeader
      ),
    policeableId
  );
}

async function updatePoliceableWithDataModifications<T>(
  update: (dataModsHeader?: DataModificationsHeader) => Promise<T>,
  policeableId: string
): Promise<T> {
  let response: T | null = null;
  await callAsyncWithDataModifications({
    changeSummary: `1 item will be modified`,
    onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
      response = await update(dataModsHeader);
    },
    apiIds: [policeableId],
    analyticsParams: {
      module: 'COLLABORATION',
      actionOrigin: ActionOrigin.COLLABORATION_MANAGEMENT_MODAL,
      modifiedAction: 'MODIFY',
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS,
  });

  const promise = makeCancelable(Promise.resolve(response) as Promise<T | null>);
  if (response == null) {
    promise.cancel();
  }
  return promise.promise as Promise<T>;
}

export function openSavingCollaborationManagementModal(policeableId: string): void {
  assert(
    isCollaboratable({id: policeableId}),
    `Object with ID ${policeableId} may not support capability. Did you add your type to CoreObjectMappings?`
  );
  runModal(SavingCollaborationManagementModal, {
    policeableId,
    onRequestSubmit: async (collaborations: Array<Collaboration>): Promise<void> => {
      await updateCollaborations(policeableId, collaborations);
    },
  });
}
